<template>
	<div
		class="row"
	>
		<div class="col-12 col-lg data-block">
			<!-- Выбор плательщика -->
			<div class="d-flex justify-content-center">
				<div class="payer-tabs mb-4 px-2 py-2">
					<div class="px-2">
						Буду оплачивать как
					</div>
					<b-button
						v-for="tab in TABS"
						:key="`$payer-tab-${tab.id}`"
						class="payer-tabs__button"
						:class="{ current: tab.value.includes(payerType) }"
						@click="payerHandler(tab.id)"
					>
						{{ tab.text }}
					</b-button>
				</div>
			</div>
			<!-- Форма поступающего -->
			<div
				v-if="block.products.length > 1"
				class="mb-4"
			>
				<div class="title mb-4">
					Выбор потока программы
				</div>
				<products-component
					v-model="products"
					class="products-list"
					:current-product="currentProduct"
					:class="{ disabled: orderHash }"
					:block="block"
					@input="productChangeHandler"
				/>
			</div>

			<div class="row mb-2">
				<div class="col-12 d-flex">
					<b-form-checkbox
						:key="`${form.is_representative}-is_representative}`"
						v-model="form.is_representative"
						class="mb-3"
						name="is_representative"
						:disabled="!!orderHash"
						@input="representativeHandler"
					>
						{{ representativeTitle }}
					</b-form-checkbox>
					<span v-if="orderHash" class="ml-3 badge badge-secondary info-badge">
						<i
							v-b-tooltip.hover
							title="Изменение недоступно, необходимо сбросить все данные"
							class="icon-info icon-icon_info"
						/>
					</span>
				</div>
			</div>
			<FormulateForm
				ref="firstStepForm"
				:key="`form-${form.is_representative}-${payerType}`"
				name="mainInfo"
				class="form-step row"
				@submit="submitHandler"
				@failed-validation="formErrorHandler"
			>
				<div
					v-if="isPhysicalPayer"
					class="col-12 mb-4"
					:class="{ 'disabled-form': isEditDisabled }"
				>
					<div class="description strong mb-5">
						Данные обучающегося
					</div>
					<div v-if="!form.is_representative" class="row mb-4">
						<FormulateInput
							v-model.trim="form.email"
							type="text"
							class="col-12 col-md-12"
							label="E-mail*"
							name="email"
							placeholder="Введите значение"
							validation-name="E-mail"
							:validation="'bail|required|email'"
							:validation-messages="{
								'required': 'обязательно к заполнению',
								'email': 'введите корректный e-mail',
							}"
							:disabled="checkFieldDisabled('email')"
						/>
						<FormulateInput
							v-model="form.last_name"
							type="text"
							label="Фамилия*"
							name="last_name"
							placeholder="Введите значение"
							class="col-12 col-md-12 col-lg-6"
							validation="bail|required|cyrillic"
							validation-name="Фамилия"
							:validation-messages="{
								'required': 'укажите фамилию',
								'cyrillic': 'Латинские символы недоступны',
							}"
							:validation-rules="{
								cyrillic: ({ value }) => validateCyrillic(value)
							}"
						/>
						<FormulateInput
							v-model="form.first_name"
							type="text"
							label="Имя*"
							name="first_name"
							placeholder="Введите значение"
							class="col-12 col-md-12 col-lg-6"
							validation="bail|required|cyrillic"
							:validation-messages="{
								'required': 'укажите имя',
								'cyrillic': 'Латинские символы недоступны',
							}"
							:validation-rules="{
								cyrillic: ({ value }) => validateCyrillic(value)
							}"
						/>
						<FormulateInput
							v-model="form.middle_name"
							type="text"
							label="Отчество*"
							name="middle_name"
							placeholder="Введите значение"
							class="col-12 col-md-12 col-lg-6"
							validation="bail|required|cyrillic"
							validation-name="Отчество"
							:validation-messages="{
								'required': 'укажите отчество',
								'cyrillic': 'Латинские символы недоступны',
							}"
							:validation-rules="{
								cyrillic: ({ value }) => validateCyrillic(value, { allowHyphens: true })
							}"
						/>
						<FormulateInput
							v-model="form.phone"
							type="CustomPhoneInput"
							label="Телефон*"
							name="phone"
							validation="bail|required|validPhone"
							placeholder="Введите значение"
							class="col-12 col-md-12 col-lg-6"
							inputmode="numeric"
							:validation-rules="{
								validPhone: () => isPhoneValid.physical
							}"
							:validation-messages="{
								'required': 'укажите телефон',
								validPhone: 'укажите корректный телефон',
							}"
							:disabled="checkFieldDisabled('phone')"
							@validatePhone="(value) => isPhoneValid.physical = value"
						/>
						<FormulateInput
							:key="`$company-name-${form.is_representative}`"
							v-model="form.company_name"
							:label="dynamicCompanyAndJob.company"
							:validation="dynamicCompanyAndJob.validation"
							type="CustomDadataCompany"
							name="company_name"
							:input_options="{placeholder: 'Введите значение'}"
							:style-classes="'user-profile-dadata-input position-relative'"
							validation-name="company_name"
							class="col-12 col-md-12 col-lg-6"
							:validation-messages="{
								'required': 'укажите название компании',
							}"
							:disabled="checkFieldDisabled('company_name')"
							@setCompanyInfo="companyHandler"
						/>
						<FormulateInput
							v-model="form.job_title"
							type="text"
							name="job_title"
							:label="dynamicCompanyAndJob.job"
							:validation="dynamicCompanyAndJob.validation"
							class="col-12 col-md-12 col-lg-6"
							validation-name="Должность"
							placeholder="Введите значение"
							:validation-messages="{
								'required': 'укажите должность',
							}"
							:disabled="checkFieldDisabled('job_title')"
						/>
						<FormulateInput
							v-model="isCompanyOptional"
							type="checkbox"
							label="Сейчас не работаю"
							name="isCompanyOptional"
							class="col-12 mb-4 small"
							data-action="isCompanyOptional"
							:disabled="!!orderHash || checkFieldDisabled('job_title')"
							@change="companyOptionalHandler"
						/>
						<FormulateInput
							v-model="form.birthday"
							type="date"
							name="birthday"
							label="Дата рождения*"
							placeholder="Выберите значение"
							validation="required"
							class="col-12 col-md-12 col-lg-6"
							:class="{ 'is-empty': !form.birthday }"
							:validation-messages="{
								'required': 'укажите дату',
							}"
							:disabled="checkFieldDisabled('birthday')"
						/>
						<FormulateInput
							v-model="form.sex"
							type="radio"
							name="sex"
							label="Пол*"
							validation="required"
							placeholder="Введите значение"
							class="sex col-12 col-md-12 col-lg-6"
							:options="{ male: 'Мужской', female: 'Женский' }"
							:validation-messages="{
								'required': 'укажите пол',
							}"
							:disabled="checkFieldDisabled('sex')"
						/>
						<FormulateInput
							v-model="form.citizenship"
							type="CustomDadataCountry"
							name="citizenship"
							label="Гражданство*"
							validation="required"
							placeholder="Введите значение"
							class="sex col-12 col-md-12 col-lg-6"
							:input_options="{
								placeholder: 'Выберите значение'
							}"
							:validation-messages="{
								required: 'необходимо выбрать значение из справочника'
							}"
							:disabled="checkFieldDisabled('citizenship')"
						/>
					</div>
					<div v-else-if="form.students.length" class="row mb-4">
						<FormulateInput
							v-model.trim="form.students[0].email"
							type="text"
							class="col-12 col-md-12"
							label="E-mail*"
							name="email"
							placeholder="Введите значение"
							validation-name="E-mail"
							validation="bail|emailExist|required|email"
							:validation-rules="{
								emailExist: ({ value }) => validateEmail(value)
							}"
							:validation-messages="{
								'required': 'обязательно к заполнению',
								'email': 'введите корректный e-mail',
								'emailExist': 'данный e-mail уже используется',
							}"
						/>
						<FormulateInput
							v-model="form.students[0].last_name"
							type="text"
							label="Фамилия*"
							name="last_name"
							placeholder="Введите значение"
							class="col-12 col-md-12 col-lg-6"
							validation="bail|required|cyrillic"
							validation-name="Фамилия"
							:validation-messages="{
								'required': 'укажите фамилию',
								'cyrillic': 'Латинские символы недоступны',
							}"
							:validation-rules="{
								cyrillic: ({ value }) => validateCyrillic(value)
							}"
						/>
						<FormulateInput
							v-model="form.students[0].first_name"
							type="text"
							label="Имя*"
							name="first_name"
							placeholder="Введите значение"
							class="col-12 col-md-12 col-lg-6"
							validation-name="Имя"
							validation="bail|required|cyrillic"
							:validation-messages="{
								'required': 'укажите имя',
								'cyrillic': 'Латинские символы недоступны',
							}"
							:validation-rules="{
								cyrillic: ({ value }) => validateCyrillic(value)
							}"
						/>
						<FormulateInput
							v-model="form.students[0].middle_name"
							type="text"
							label="Отчество*"
							name="middle_name"
							placeholder="Введите значение"
							class="col-12 col-md-12 col-lg-6"
							validation="bail|required|cyrillic"
							validation-name="Отчество"
							:validation-messages="{
								'required': 'укажите отчество',
								'cyrillic': 'Латинские символы недоступны',
							}"
							:validation-rules="{
								cyrillic: ({ value }) => validateCyrillic(value, { allowHyphens: true })
							}"
						/>
						<FormulateInput
							v-model="form.students[0].phone"
							type="CustomPhoneInput"
							label="Телефон*"
							name="phone"
							validation="bail|required|validPhone"
							placeholder="Введите значение"
							class="col-12 col-md-12 col-lg-6"
							inputmode="numeric"
							:validation-rules="{
								validPhone: () => isPhoneValid.physical
							}"
							:validation-messages="{
								'required': 'укажите телефон',
								validPhone: 'укажите корректный телефон',
							}"
							@validatePhone="(value) => isPhoneValid.physical = value"
						/>
						<FormulateInput
							v-model="form.students[0].company_name"
							label="Компания*"
							name="company_name"
							type="CustomDadataCompany"
							:input_options="{placeholder: 'Введите значение'}"
							:style-classes="'user-profile-dadata-input position-relative'"
							class="col-12 col-md-12 col-lg-6"
							validation="required"
							:validation-messages="{
								'required': 'укажите название компании',
							}"
						/>
						<FormulateInput
							v-model="form.students[0].job_title"
							type="text"
							label="Должность*"
							name="job_title"
							class="col-12 col-md-12 col-lg-6"
							validation-name="Должность"
							validation="required"
							placeholder="Введите значение"
							:validation-messages="{
								'required': 'укажите должность',
							}"
						/>
						<FormulateInput
							v-model="form.students[0].birthday"
							type="date"
							label="Дата рождения*"
							name="birthday"
							placeholder="Выберите значение"
							validation="required"
							class="col-12 col-md-12 col-lg-6"
							:class="{ 'is-empty': !form.students[0].birthday }"
							:validation-messages="{
								'required': 'укажите дату',
							}"
						/>
						<FormulateInput
							v-model="form.students[0].sex"
							type="radio"
							name="sex"
							label="Пол*"
							validation="required"
							placeholder="Введите значение"
							class="sex col-12 col-md-12 col-lg-6"
							:options="{ male: 'Мужской', female: 'Женский' }"
							:validation-messages="{
								'required': 'укажите пол',
							}"
						/>
						<FormulateInput
							v-model="form.students[0].citizenship"
							type="CustomDadataCountry"
							name="citizenship"
							label="Гражданство*"
							validation="required"
							placeholder="Введите значение"
							class="sex col-12 col-md-12 col-lg-6"
							:input_options="{
								placeholder: 'Выберите значение'
							}"
							:validation-messages="{
								required: 'необходимо выбрать значение из справочника'
							}"
						/>
						<div class="col-12">
							<b-form-checkbox
								v-model="form.students[0].no_education"
								v-b-tooltip.hover
								tabindex="0"
								value="1"
								unchecked-value="0"
								name="no_education"
								class="col-12 mb-3 no-diplom"
								@input="syncFormField('students')"
							>
								Нет диплома об образовании
							</b-form-checkbox>
						</div>
					</div>
				</div>
				<div
					v-else
					class="mb-4 col-12"
					:class="{ 'disabled-form': isEditDisabled }"
				>
					<div class="row">
						<div class="col-12">
							<div class="row">
								<div class="col-12 d-flex">
									<b-form-checkbox
										v-if="!form.is_representative"
										v-model="form.no_education"
										v-b-tooltip.hover
										tabindex="0"
										value="1"
										unchecked-value="0"
										class="mb-3 no-diplom"
										name="no_education"
										:disabled="orderHash"
										@input="legalNoEducationHandler"
									>
										{{ legalNoEducationTitle }}
									</b-form-checkbox>
									<b-form-checkbox
										v-else
										v-model="studentsNoEducation"
										v-b-tooltip.hover
										tabindex="0"
										value="1"
										unchecked-value="0"
										class="mb-3 no-diplom"
										name="no_education"
										:disabled="orderHash"
										@input="legalNoEducationHandler"
									>
										{{ legalNoEducationTitle }}
									</b-form-checkbox>
									<span v-if="orderHash" class="ml-3 badge badge-secondary info-badge">
										<i
											v-b-tooltip.hover
											title="Изменение недоступно, необходимо сбросить все данные"
											class="icon-info icon-icon_info"
										/>
									</span>
								</div>
							</div>
						</div>
					</div>
					<template v-if="!form.is_representative">
						<div class="description strong mb-5">
							Обучающийся № 1
						</div>
						<div class="row mb-4">
							<FormulateInput
								v-model.trim="form.email"
								type="text"
								class="col-12 col-md-12"
								label="E-mail студента 1*"
								name="email"
								placeholder="Введите значение"
								validation-name="E-mail"
								:validation="'bail|required|email'"
								:validation-messages="{
									'required': 'обязательно к заполнению',
									'email': 'введите корректный e-mail',
								}"
								:disabled="checkFieldDisabled('email')"
							/>
							<FormulateInput
								v-model="form.last_name"
								type="text"
								label="Фамилия*"
								name="last_name"
								placeholder="Введите значение"
								class="col-12 col-md-12 col-lg-6"
								validation="bail|required|cyrillic"
								validation-name="Фамилия"
								:validation-messages="{
									'required': 'укажите фамилию',
									'cyrillic': 'Латинские символы недоступны',
								}"
								:validation-rules="{
									cyrillic: ({ value }) => validateCyrillic(value)
								}"
							/>
							<FormulateInput
								v-model="form.first_name"
								type="text"
								label="Имя*"
								name="first_name"
								placeholder="Введите значение"
								class="col-12 col-md-12 col-lg-6"
								validation-name="Имя"
								validation="bail|required|cyrillic"
								:validation-messages="{
									'required': 'укажите имя',
									'cyrillic': 'Латинские символы недоступны',
								}"
								:validation-rules="{
									cyrillic: ({ value }) => validateCyrillic(value)
								}"
							/>
							<FormulateInput
								v-model="form.middle_name"
								type="text"
								label="Отчество*"
								name="middle_name"
								placeholder="Введите значение"
								class="col-12 col-md-12 col-lg-6"
								validation="bail|required|cyrillic"
								validation-name="Отчество"
								:validation-messages="{
									'required': 'укажите отчество',
									'cyrillic': 'Латинские символы недоступны',
								}"
								:validation-rules="{
									cyrillic: ({ value }) => validateCyrillic(value, { allowHyphens: true })
								}"
							/>
							<FormulateInput
								v-model="form.phone"
								type="CustomPhoneInput"
								label="Телефон*"
								name="phone"
								validation="bail|required|validPhone"
								placeholder="Введите значение"
								class="col-12 col-md-12 col-lg-6"
								inputmode="numeric"
								:validation-rules="{
									validPhone: () => isPhoneValid.physical
								}"
								:validation-messages="{
									'required': 'укажите телефон',
									validPhone: 'укажите корректный телефон',
								}"
								:disabled="checkFieldDisabled('phone')"
								@validatePhone="(value) => isPhoneValid.physical = value"
							/>
							<FormulateInput
								v-model="form.company_name"
								label="Компания*"
								name="company_name"
								type="CustomDadataCompany"
								:input_options="{placeholder: 'Введите значение'}"
								:style-classes="'user-profile-dadata-input position-relative'"
								validation="required"
								validation-name="company_name"
								class="col-12 col-md-12 col-lg-6"
								:validation-messages="{
									'required': 'укажите название компании',
								}"
								:disabled="checkFieldDisabled('company_name')"
							/>
							<FormulateInput
								v-model="form.job_title"
								type="text"
								label="Должность*"
								name="job_title"
								class="col-12 col-md-12 col-lg-6"
								validation-name="Должность"
								validation="required"
								placeholder="Введите значение"
								:validation-messages="{
									'required': 'укажите должность',
								}"
								:disabled="checkFieldDisabled('job_title')"
							/>
							<FormulateInput
								v-model="form.birthday"
								type="date"
								label="Дата рождения*"
								name="birthday"
								placeholder="Выберите значение"
								validation="required"
								class="col-12 col-md-12 col-lg-6"
								:class="{ 'is-empty': !form.birthday }"
								:validation-messages="{
									'required': 'укажите дату',
								}"
								:disabled="checkFieldDisabled('birthday')"
							/>
							<FormulateInput
								v-model="form.sex"
								type="radio"
								name="sex"
								label="Пол*"
								validation="required"
								placeholder="Введите значение"
								class="sex col-12 col-md-12 col-lg-6"
								:options="{ male: 'Мужской', female: 'Женский' }"
								:validation-messages="{
									'required': 'укажите пол',
								}"
								:disabled="checkFieldDisabled('sex')"
							/>
							<FormulateInput
								v-model="form.citizenship"
								type="CustomDadataCountry"
								name="citizenship"
								label="Гражданство*"
								validation="required"
								placeholder="Введите значение"
								class="sex col-12 col-md-12 col-lg-6"
								:input_options="{
									placeholder: 'Выберите значение'
								}"
								:validation-messages="{
									required: 'необходимо выбрать значение из справочника'
								}"
								:disabled="checkFieldDisabled('citizenship')"
							/>
						</div>
					</template>
					<template v-for="(student, key) in form.students">
						<div :key="`student-title-${key}`" class="description strong mb-5">
							Обучающийся № {{ getStudentKey(key) }}
						</div>
						<div :key="`student-form-${key}`" class="row mb-4">
							<FormulateInput
								v-model="student.email"
								type="text"
								class="col-12 col-md-12 col-lg-6"
								:label="`E-mail студента ${getStudentKey(key)}*`"
								name="student_email"
								placeholder="Введите значение"
								:validation="'bail|required|emailExist|email'"
								:validation-rules="{
									emailExist: ({ value }) => validateEmail(value)
								}"
								:validation-messages="{
									'required': 'обязательно к заполнению',
									'email': 'введите корректный e-mail',
									'emailExist': 'данный e-mail уже введен',
								}"
							/>
							<FormulateInput
								v-model="student.last_name"
								type="text"
								label="Фамилия*"
								name="student_last_name"
								placeholder="Введите значение"
								class="col-12 col-md-12 col-lg-6"
								validation="bail|required|cyrillic"
								:validation-messages="{
									'required': 'укажите фамилию',
									'cyrillic': 'Латинские символы недоступны',
								}"
								:validation-rules="{
									cyrillic: ({ value }) => validateCyrillic(value)
								}"
							/>
							<FormulateInput
								v-model="student.first_name"
								type="text"
								label="Имя*"
								name="student_first_name"
								placeholder="Введите значение"
								class="col-12 col-md-12 col-lg-6"
								validation="bail|required|cyrillic"
								:validation-messages="{
									'required': 'укажите имя',
									'cyrillic': 'Латинские символы недоступны',
								}"
								:validation-rules="{
									cyrillic: ({ value }) => validateCyrillic(value)
								}"
							/>
							<FormulateInput
								v-model="student.middle_name"
								type="text"
								label="Отчество*"
								name="student_middle_name"
								placeholder="Введите значение"
								class="col-12 col-md-12 col-lg-6"
								validation="bail|required|cyrillic"
								:validation-messages="{
									'required': 'укажите отчество',
									'cyrillic': 'Латинские символы недоступны',
								}"
								:validation-rules="{
									cyrillic: ({ value }) => validateCyrillic(value, { allowHyphens: true })
								}"
							/>
							<FormulateInput
								v-model="student.phone"
								type="CustomPhoneInput"
								name="student_phone"
								label="Телефон*"
								validation="bail|required|validPhone"
								placeholder="Введите значение"
								class="col-12 col-md-12 col-lg-6"
								inputmode="numeric"
								:validation-rules="{
									validPhone: () => isPhoneValid.physical
								}"
								:validation-messages="{
									'required': 'укажите телефон',
									validPhone: 'укажите корректный телефон',
								}"
								@validatePhone="(value) => isPhoneValid.physical = value"
							/>
							<FormulateInput
								v-model="student.company_name"
								label="Компания*"
								name="student_company_name"
								type="CustomDadataCompany"
								:input_options="{placeholder: 'Введите значение'}"
								:style-classes="'user-profile-dadata-input position-relative'"
								validation="required"
								class="col-12 col-md-12 col-lg-6"
								:validation-messages="{
									'required': 'укажите название компании',
								}"
							/>
							<FormulateInput
								v-model="student.job_title"
								type="text"
								label="Должность*"
								name="student_job_title"
								class="col-12 col-md-12 col-lg-6"
								validation="required"
								placeholder="Введите значение"
								:validation-messages="{
									'required': 'укажите должность',
								}"
							/>
							<FormulateInput
								v-model="student.birthday"
								type="date"
								label="Дата рождения*"
								name="student_birthday"
								placeholder="Выберите значение"
								validation="required"
								class="col-12 col-md-12 col-lg-6"
								:class="{ 'is-empty': !student.birthday }"
								:validation-messages="{
									'required': 'укажите дату',
								}"
							/>
							<FormulateInput
								v-model="student.citizenship"
								type="CustomDadataCountry"
								label="Гражданство*"
								name="student_citizenship"
								validation="required"
								placeholder="Введите значение"
								class="sex col-12 col-md-12 col-lg-6"
								:input_options="{
									placeholder: 'Выберите значение'
								}"
								:validation-messages="{
									required: 'необходимо выбрать значение из справочника'
								}"
							/>
							<FormulateInput
								v-model="student.sex"
								type="radio"
								:name="`student_sex_${key}`"
								label="Пол*"
								validation="required"
								placeholder="Введите значение"
								class="sex col-12 col-md-12 col-lg-6"
								:options="{ male: 'Мужской', female: 'Женский' }"
								:validation-messages="{
									'required': 'укажите пол',
								}"
							/>
						</div>
					</template>
					<div v-if="!orderHash" class="form-step__footer row d-flex justify-content-center">
						<div
							v-if="isShowRemoveStudent"
							class="col-8 col-lg-4 btn btn-outline-danger"
							@click="removeStudent"
						>
							Удалить
						</div>
						<div
							class="col-8 col-lg-4 btn btn-outline-primary"
							@click="addStudent($event)"
						>
							Добавить обучающихся
						</div>
					</div>
				</div>

				<div class="mb-4 col-12">
					<FormulateInput
						v-if="showPersonalData"
						v-model="agreements.personal_data"
						class="mb-3 small"
						type="checkbox"
						validation="required"
						name="agreementPersonalData"
						:validation-messages="{
							required: $t('agreementRequired')
						}"
						data-action="agreementPersonalData"
					>
						<template #label="{ id }">
							<label :for="id" class="ml-2">
								Даю
								<a
									:href="`https://www.skolkovo.ru/static/personaldata_rus.html`"
									target="_blank"
								>
									согласие на обработку персональных данных
								</a>
							</label>
						</template>
					</FormulateInput>

					<loyalty-registration-input
						@change="(value) => agreements.loyalty_agreement = value"
					/>

					<b2b-alumni-input v-if="block.showb2bAlumniInput" />
				</div>
				<div class="col-12 d-flex flex-column justify-content-start">
					<div class="w-100 d-flex">
						<FormulateInput
							id="submit"
							type="submit"
							class="submit mb-2 w-100"
							:disabled="isLoading"
						>
							<span v-if="isLoading">
								<b-spinner small />
							</span>
							<span v-else>Продолжить</span>
						</FormulateInput>
					</div>
					<div v-if="errors.submit" class="text-danger mb-2">
						{{ errors.submit }}
					</div>
					<CookieError v-if="isErrorCookie" />
				</div>
			</FormulateForm>
		</div>
	</div>
</template>

<script>
import b2bAlumniInput from '~/components/panels/formulate/products/components/b2bAlumniInput.vue';
import ProductsComponent from '~/components/panels/formulate/products/ProductsComponent.vue';
import LoyaltyRegistrationInput from '~/components/panels/formulate/products/components/LoyaltyRegistrationInput.vue';
import DaDataFullCompanyItem from '~/components/daData/DadataItems/DaDataFullCompanyItem.vue';
import CookieError from '~/components/panels/formulate/products/components/CookieError.vue';
import paymentFormMixin from '~/mixins/paymentFormMixin';

import { FORM_PAYER_TYPE } from '~/utils/dictionaries';
import {
	mapActions, mapGetters, mapMutations, mapState,
} from 'vuex';
import moment from '~/utils/moment';
import formulateFormScenario from '~/mixins/formulateFormScenario';

const FORM_FIELDS = [
	'email',
	'last_name',
	'first_name',
	'middle_name',
	'phone',
	'company_name',
	'inn',
	'job_title',
	'birthday',
	'sex',
	'students',
	'no_education',
	'is_representative',
	'citizenship',
];

const TABS = [
	{
		id: FORM_PAYER_TYPE.PHYSICAL,
		value: [FORM_PAYER_TYPE.PHYSICAL],
		text: 'Физическое лицо',
	},
	{
		id: FORM_PAYER_TYPE.URIDIC,
		value: [FORM_PAYER_TYPE.URIDIC, FORM_PAYER_TYPE.IP],
		text: 'Юридическое лицо и ИП',
	},
];

export default {
	name: 'FirstStep',
	components: {
		LoyaltyRegistrationInput,
		ProductsComponent,
		b2bAlumniInput,
		CookieError,
	},
	mixins: [paymentFormMixin, formulateFormScenario],
	props: {
		block: { type: Object, required: true },
		order: { type: Object, default: () => ({}) },
	},
	data() {
		return {
			TABS,
			form: {
				email: null,
				last_name: null,
				first_name: null,
				middle_name: null,
				phone: null,
				company_name: null,
				inn: null,
				job_title: null,
				birthday: null,
				sex: null,
				no_education: null,
				citizenship: null,
				is_representative: false,
				payer: {},
				students: [],
			},
			isPhoneValid: {
				physical: false,
				legal: false,
			},
			isVerifiedPhone: false,
			isCompanyOptional: false,
			studentsNoEducation: null,
			errors: {
				submit: '',
			},
			DaDataFullCompanyItem,
		};
	},
	computed: {
		...mapState('scenarios/paymentForm', [
			'isLoading',
			'orderHash',
			'isCompletedFieldsDisabled',
			'currentProduct',
			'personData',
		]),
		...mapGetters('scenarios/paymentForm', [
			'baseForm',
			'payerType',
			'isIpPayer',
			'isPhysicalPayer',
			'isRepresentative',
			'currentRedirectStep',
			'isHideDocumentsStep',
		]),
		agreementsDocs() {
			const product = this.order?.data?.products[0];
			return product?.agreements;
		},
		payerTypeLocal: {
			get() {
				return this.payerType;
			},
			set(value) {
				this.form.payer.type = value;
				this.setPayerType(value);
			},
		},
		representativeTitle() {
			const physical = 'Хочу зарегистрировать не себя';
			const legal = 'Не буду обучаться сам(а), регистрирую коллег';
			return this.isPhysicalPayer ? physical : legal;
		},
		dynamicCompanyAndJob() {
			const suffix = !this.isCompanyOptional ? '*' : '';
			return {
				company: `Компания${suffix}`,
				job: `Должность${suffix}`,
				validation: !this.isCompanyOptional ? 'required' : null,
			};
		},
		legalNoEducationTitle() {
			return `Нет ${this.form.students.length ? 'дипломов' : 'диплома'} об образовании`;
		},
		preparedBaseForm() {
			const preparedForm = {
				...this.baseForm,
				email: this.baseForm.email.toLowerCase(),
			};

			if (this.form.students?.length) {
				// Нормализуем email студентов
				preparedForm.students = this.baseForm.students.map((student) => ({
					...student,
					email: student.email?.toLowerCase(),
				}));

				if (!this.form.is_representative) {
					const {
						students, payer, is_representative, ...representativeData
					} = this.form;

					// Объединяем студентов с данными представителя
					return {
						...preparedForm,
						students: [
							...preparedForm.students,
							representativeData,
						],
					};
				}
			}
			return preparedForm;
		},
		isShowRemoveStudent() {
			const { students } = this.form;

			if (!this.form.is_representative) return students.length;
			return students.length > 1;
		},
		isEditDisabled() {
			return this.order.data?.simple_sending_status;
		},
	},
	watch: {
		'$auth.user': {
			handler(value) {
				if (value) {
					this.autocompleteLocalForm(this.form, FORM_FIELDS, value);
				}
			},
			immediate: true,
		},
		personData: {
			handler(value) {
				if (!value) return;
				const students = value.user_data?.students || [];

				if (!students?.length) return;

				const { is_representative: isRepresentative, no_education: noEducation } = this.personData;
				const isNoEducation = +noEducation || 0;

				this.form.is_representative = isRepresentative;
				this.form.students = isRepresentative ? students : students.slice(0, -1);

				this.setStudentNoEducation(isNoEducation);

				if (isRepresentative) {
					this.studentsNoEducation = isNoEducation;
				} else {
					this.form.no_education = isNoEducation;
				}
			},
			immediate: true,
		},
	},
	mounted() {
		this.getPhoneVerification();
	},
	methods: {
		...mapActions('scenarios/paymentForm', [
			'setCurrentStep',
		]),
		...mapMutations('scenarios/paymentForm', [
			'setPayerType',
			'setOrderHash',
			'setLoading',
			'setStepError',
		]),
		getPrepareData() {
			const product = this.currentProduct.id ? this.currentProduct : this.block.products[0];
			const currentProduct = this.order?.data?.products[0] || product;
			const userData = {
				sklTargetAction: this.block.form_scenario_action_label,
				sklBirthDate: this.form.birthday ? moment(this.form.birthday).format('DD.MM.YYYY') : '',
				...this.preparedBaseForm,
				...this.getAnalyticsData(),
			};

			const data = {
				user_data: userData,
				products: [currentProduct],
				entity_name: this.fetchedData.entity_name,
				entity_id: this.fetchedData.id,
			};
			window.dataLayer = window.dataLayer || [];
			window.dataLayer.push({
				event: 'form_send',
				form_data: data,
			});

			const formData = new FormData();
			this.createFormData(formData, data);

			return formData;
		},
		async sendForm() {
			const formData = this.getPrepareData();
			const response = await this.$api.submitFormulateForm(this.block.scenario_id, formData);

			if (response.url) {
				if (response.url.includes('https://')) {
					window.location = response.url;
					return;
				}
				const scenarioUserData = JSON.parse(response.url);
				this.setOrderHash(scenarioUserData.hash);
			}
		},
		async updatePerson() {
			const formData = this.getPrepareData();
			await this.$api.syncPersonOnPayment(this.orderHash, formData);
		},
		async submitHandler() {
			this.errors.submit = '';

			if (!this.isCookiesAccepted) {
				this.showCookieError();
				this.isErrorCookie = true;
				return;
			}

			try {
				this.setLoading(true);

				this.syncLocalForm();
				// eslint-disable-next-line no-unused-expressions
				this.orderHash ? await this.updatePerson() : await this.sendForm();
				this.setGlobalAgreementPersonalData();

				if (this.currentRedirectStep) {
					await this.setCurrentStep(this.currentRedirectStep);
					return;
				}
				if (!this.isPhysicalPayer) {
					await this.setCurrentStep(this.STEPS.COMPANY);
					return;
				}

				const step = this.form.is_representative ? this.STEPS.PAYMENT : this.STEPS.DOCUMENTS;
				await this.setCurrentStep(step);
			} catch (e) {
				console.error(e);
				const { message } = e.response?.data;
				this.errors.submit = message || 'Произошла ошибка при отправке формы';

				this.setStepError(true);
			} finally {
				this.setLoading(false);
			}
		},
		payerHandler(id) {
			if (this.checkOrderHashAndReset()) return;

			this.form.students = [];
			this.form.is_representative = false;
			this.form.no_education = null;
			this.studentsNoEducation = null;
			this.payerTypeLocal = id;

			this.syncLocalForm();
			this.$formulate.resetValidation('mainInfo');
		},
		autocompletePayerForm() {
			if (!this.order?.data) return;
			const { payer } = this.order.data.user_data;

			this.form.payer.phone = payer.phone;
			this.form.payer.email = payer.email;
			this.form.payer.edo_id = payer.edo_id;
			this.form.payer.name = payer.name;
			this.form.payer.kpp = payer.kpp;
		},
		companyHandler(value) {
			this.form.inn = value?.data?.inn ?? null;
		},
		payerCompanyHandler(val) {
			if (val && val.value) {
				this.form.payer.name = val.value;
				if (val.data) {
					if (val.data.address) {
						this.form.payer.address = val.data.address.value;
						this.form.payer.index = val.data.address.data?.postal_code;
					}
					this.form.payer.inn = val.data.inn;
					this.form.payer.ogrn = val.data.ogrn;
					this.form.payer.kpp = val.data.kpp;
					this.form.payer.name = val.value;
				}
			}
		},
		companyOptionalHandler(value) {
			if (!value) return;

			this.form.company_name = null;
			this.form.inn = null;
			this.form.job_title = null;
		},
		async setGlobalAgreementPersonalData() {
			const { user } = this.$auth;
			const data = {
				// Значение из mixin agreements
				is_agrees_to_processing_personal_data: this.agreements?.personal_data,
				is_agrees_to_loyalty_participation: this.agreements?.loyalty_agreement,
			};

			await this.$api.setAgreementFields(data, user.id);
			await this.$auth.fetchUser({ url: 'api/auth/users/self?full=true' });
			this.fetchAgreements();
		},
		representativeHandler(value) {
			value ? this.addStudent() : this.form.students = [];
			this.syncLocalForm();
			this.$formulate.resetValidation('mainInfo');
		},
		syncFormField(field) {
			this.syncForm([field], { ...this.form });
		},
		addStudent(event) {
			if (event) event.preventDefault();
			const noEducation = this.form.is_representative
				? this.studentsNoEducation
				: this.form.no_education;

			this.form.students.push({});
			this.setStudentNoEducation(noEducation);
			this.syncFormField('students');
		},
		removeStudent() {
			this.form.students.pop();
			this.syncLocalForm();
		},
		syncLocalForm() {
			this.syncForm(FORM_FIELDS, { ...this.form });
		},
		validate() {
			this.$formulate.submit('mainInfo');
		},
		checkFieldDisabled(key) {
			const errors = this.$refs.firstStepForm?.registry?.errors;
			const hasError = errors?.[key];
			const isFilled = !!this.form[key];

			if (key === 'email' && isFilled) return isFilled;
			if (key === 'phone' && this.isVerifiedPhone && isFilled) return isFilled;

			if (
				!this.isCompletedFieldsDisabled
				|| !this.autoFields.includes(key)
				|| this.orderHash
				|| hasError
			) {
				return false;
			}
			return isFilled;
		},
		async getPhoneVerification() {
			try {
				const res = await this.$api.getIsVerifiedPhone();
				this.isVerifiedPhone = res.status;
			} catch (error) {
				console.log(error);
			} finally {
				this.isVerifiedPhoneFetching = false;
			}
		},
		setStudentNoEducation(value) {
			this.form.students = this.form.students
				.map((student) => ({ ...student, no_education: value }));
			this.syncLocalForm();
		},
		legalNoEducationHandler(value) {
			if (this.form.students.length) {
				this.setStudentNoEducation(value);
			}
			this.form.no_education = value;
			this.studentsNoEducation = value;
			this.syncLocalForm();
		},
		formErrorHandler() {
			this.setStepError(true);
		},
		validateEmail(value) {
			const email = value?.toLowerCase();
			const emailExistInForm = email === this.form.email?.toLowerCase();
			const emailExistInStudents = this.form.students
				.filter((student) => student.email?.toLowerCase() === email);

			return !(emailExistInForm || emailExistInStudents.length > 1);
		},
		getStudentKey(key) {
			return key + (this.form.is_representative ? 1 : 2);
		},
	},
};

</script>

<style lang="scss" scoped>
@import '~/assets/styles/sk_variables';
@import "~/assets/styles/bootstrap/scss/functions";
@import "~/assets/styles/bootstrap/scss/variables";
@import "~/assets/styles/bootstrap/scss/mixins";

@mixin disabled {
	opacity: .7;
	pointer-events: none;
}

.payer-tabs {
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: .5rem;
	justify-content: space-between;
	background-color: $theme-light;
	border-radius: 3rem;
	padding: 0.5rem;

	&__button {
		color: $border-color;
		background-color: transparent;
		border-color: transparent;
		border-radius: 3rem;
		border: none;

		&:focus, &:active {
			border: none;
			box-shadow: none;
		}

		:hover {
			border-color: $border-color;
		}

		&.current {
			color: white;
			background-color: $blue;
			border-radius: 3rem;
		}

	}
	@include media-breakpoint-down(sm) {
		button {
			font-size: 0.8rem;
			font-weight: 500;
			padding: 0.375rem 0.45rem;
			border-radius: 3rem;
		}
	}

	.description {
		font-size: 1.2rem;
	}
}

.btn.btn-secondary:not(.disabled):hover {
	border-color: $border-color;
	color: #fff;
}
.btn.btn-secondary:not(.current) {
	background-color: transparent;
}
.btn.btn-secondary:not(.current):hover {
	background-color: rgba(7, 15, 36, 0.2);
}

.form-step {
	&.disabled {
		.formulate-input-wrapper, .formulate-input-element, .formulate-input-element-decorator {
			@include disabled;
		}
	}

	.formulate-input[data-classification=text] input:disabled {
		pointer-events: none;
		background-color: unset;
		color: #7183d3;
	}

	.formulate-input-element:has(input[type="radio"]:disabled) {
		opacity: .7;
	}

	&__footer {
		display: flex;
		gap: 1rem;
	}
}

.products-list {
	&.disabled {
		@include disabled;
	}
}

.is-empty {
	input[type="date"] {
		color: $border-color !important;
	}
}

.disabled-form {
	@include disabled;
}

.info-badge {
	width: 1.5rem;
	height: 1.5rem;
	display: flex;
	justify-content: center;
	align-items: center;
	font-size: 0.8rem;
	border-radius: 50%;
	cursor: pointer;
}
</style>
